import React from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';

import type { RetrievalGraphNodeData } from '../../../types/retrieval';

const entityColor: Record<string, string> = {
  Project: 'border-l-blue-500',
  Person: 'border-l-green-500',
  Methodology: 'border-l-amber-500',
  Industry: 'border-l-blue-400',
  Document: 'border-l-neutral-400',
};

const confidenceColor = (value?: number): string => {
  if (typeof value !== 'number') {
    return 'bg-neutral-300';
  }
  if (value >= 0.75) {
    return 'bg-green-500';
  }
  if (value >= 0.5) {
    return 'bg-amber-500';
  }
  return 'bg-red-500';
};

const EntityNode: React.FC<NodeProps<Node<RetrievalGraphNodeData>>> = ({ data, selected }) => {
  const borderClass = entityColor[data.entityType] || 'border-l-neutral-400';

  return (
    <div
      className={`min-w-[180px] rounded-xl border border-neutral-200 border-l-4 bg-white px-3 py-2 shadow-sm transition-all ${borderClass} ${
        selected ? 'ring-2 ring-blue-300 shadow-md' : 'hover:shadow-md'
      }`}
    >
      <Handle type="target" position={Position.Left} className="!h-2 !w-2 !bg-neutral-400" />
      <div className="text-xs font-semibold text-neutral-900">{data.label}</div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
        {data.entityType}
      </div>
      {typeof data.confidence === 'number' ? (
        <div className="mt-2 flex items-center gap-2">
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-neutral-200">
            <div
              className={`h-full rounded-full ${confidenceColor(data.confidence)}`}
              style={{ width: `${Math.round(Math.max(0, Math.min(1, data.confidence)) * 100)}%` }}
            />
          </div>
          <span className="text-[10px] text-neutral-500">{Math.round(data.confidence * 100)}%</span>
        </div>
      ) : null}
      <Handle type="source" position={Position.Right} className="!h-2 !w-2 !bg-neutral-400" />
    </div>
  );
};

export default React.memo(EntityNode);
